import { useState, useEffect, useRef } from "react";
import { Phone, Mail, ChevronDown, CheckCircle2, XCircle, Shield, FileCheck, Zap, Award, ArrowRight, AlertTriangle, Battery, Sun, Gauge, Settings, Wifi, Home, ChevronLeft, ChevronRight, MessageCircle, MapPin, ListChecks, Send, HelpCircle, User } from "lucide-react";
import useSeo from "./useSeo";

const PrivatkundenPage = () => {
  useSeo({
    title: "Für Privatkunden | PV-Abnahme & Inbetriebnahme – ENTHIDA",
    description: "Ihr PV-Spezialist für Privatkunden: VDE-konforme Abnahme, Inbetriebnahme, Wallbox und Speicher – strukturiert, normgerecht und verständlich erklärt.",
    path: "/fuer-privatkunden",
  });
  const [openFaq, setOpenFaq] = useState(null);
  const [isHeroContactOpen, setIsHeroContactOpen] = useState(false);
  const [activeRefCategory, setActiveRefCategory] = useState("alle");
  const [currentRefSlide, setCurrentRefSlide] = useState(0);
  const heroContactRef = useRef(null);

  // Anlagen-Check Form State (B2C)
  const [form, setForm] = useState({
    status: "",
    groesse: "",
    speicher: "",
    termin: "",
    themen: [],
    name: "",
    ort: "",
    nachricht: "",
  });

  const toggleThema = (val) => {
    setForm((prev) => ({
      ...prev,
      themen: prev.themen.includes(val)
        ? prev.themen.filter((t) => t !== val)
        : [...prev.themen, val],
    }));
  };

  const buildMessage = () => {
    const lines = [
      "Neue Anfrage über enthida.de (Privatkunde)",
      "",
      `Anlagenstatus: ${form.status || "—"}`,
      `Anlagengröße: ${form.groesse || "—"}`,
      `Speicher: ${form.speicher || "—"}`,
      `Wunschtermin: ${form.termin || "—"}`,
      `Worum geht's: ${form.themen.length ? form.themen.join(", ") : "—"}`,
      `Name: ${form.name || "—"}`,
      `Ort: ${form.ort || "—"}`,
    ];
    if (form.nachricht.trim()) {
      lines.push("", "Nachricht:", form.nachricht.trim());
    }
    return lines.join("\n");
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/4917641234614?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleEmail = () => {
    const subject = "Anlagen-Check Anfrage (Privatkunde)";
    const url = `mailto:info@enthida.de?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildMessage())}`;
    window.location.href = url;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (heroContactRef.current && !heroContactRef.current.contains(event.target)) {
        setIsHeroContactOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-slide for references
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentRefSlide((prev) => (prev + 1) % filteredReferences.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [activeRefCategory]);

  const processSteps = [
    { num: "01", title: "Erstkontakt", desc: "Kurzes Gespräch und erste Einschätzung Ihres Projekts", icon: Phone },
    { num: "02", title: "Vor-Ort-Termin", desc: "Prüfung der Anlage und des Zählerschranks", icon: Shield },
    { num: "03", title: "Umsetzung", desc: "Anschluss, Messungen und Abnahme", icon: Zap },
    { num: "04", title: "Dokumentation", desc: "Prüfprotokolle und alle notwendigen Unterlagen", icon: FileCheck },
    { num: "05", title: "Inbetriebnahme", desc: "Kundeneinweisung – Sie verstehen Ihre Anlage", icon: CheckCircle2 },
  ];

  const pruefpunkte = [
    { title: "Zählerschrank & TAB-Konformität", desc: "Überprüfung nach technischen Anschlussbedingungen des Netzbetreibers" },
    { title: "Überspannungsschutz", desc: "Schutz vor Blitzeinwirkung und Überspannungen – Pflicht bei PV-Anlagen" },
    { title: "Potentialausgleich", desc: "Korrekte Erdung aller Komponenten für maximale Sicherheit" },
    { title: "Leitungsquerschnitte", desc: "Dimensionierung nach Belastung und Leitungslänge" },
    { title: "Schutzmaßnahmen", desc: "FI-Schutzschalter, Leitungsschutzschalter, Brandschutz" },
    { title: "Wechselrichter-Einstellungen", desc: "Parametrierung nach Netzbetreiber-Vorgaben" },
    { title: "Smart Meter Vorbereitung", desc: "Platz und Infrastruktur für intelligente Messsysteme" },
    { title: "Netzbetreiber-Vorgaben", desc: "Einhaltung aller lokalen Anschlussbedingungen" },
    { title: "Dokumentation", desc: "Prüfprotokolle, Anlagenschema, Inbetriebnahmeprotokoll" },
    { title: "VDE-Konformität", desc: "Prüfung nach aktuellen VDE-Normen (0100, 0126, AR-N 4105)" },
    { title: "Monitoring & Kommunikation", desc: "Einrichtung der Überwachung und Fernzugriff" },
  ];

  const energiesysteme = [
    { icon: Battery, title: "PV-Speicher", desc: "Batteriespeicher für maximalen Eigenverbrauch und Unabhängigkeit" },
    { icon: Zap, title: "Wallbox Installation", desc: "Ladestation für Ihr Elektrofahrzeug – intelligent und sicher" },
    { icon: Sun, title: "Heizstab-Überschuss", desc: "Überschüssigen PV-Strom für Warmwasser nutzen" },
    { icon: Gauge, title: "Lastmanagement", desc: "Intelligente Verteilung der Energie im Haushalt" },
    { icon: Settings, title: "Smart Meter", desc: "Intelligente Messsysteme für transparenten Verbrauch" },
    { icon: Wifi, title: "Energiemonitoring", desc: "Echtzeit-Überwachung Ihrer Energieflüsse" },
    { icon: Home, title: "Wärmepumpen-Vorbereitung", desc: "Infrastruktur für zukünftige Wärmepumpe" },
    { icon: Award, title: "Eigenverbrauchsoptimierung", desc: "Maximieren Sie die Nutzung Ihres selbst erzeugten Stroms" },
  ];

  const probleme = [
    { title: "Fehlender Überspannungsschutz", desc: "Gefahr für die gesamte Anlage bei Blitzeinwirkung. Oft bei älteren Installationen nicht vorhanden." },
    { title: "Veralteter Zählerschrank", desc: "Kein Platz für moderne Komponenten, nicht TAB-konform, keine Erweiterungsmöglichkeit." },
    { title: "Fehlende Dokumentation", desc: "Ohne Prüfprotokolle keine Abnahme durch Netzbetreiber. Probleme bei Versicherungsfällen." },
    { title: "Unsaubere AC-Installation", desc: "Falsche Querschnitte, fehlende Beschriftung, unübersichtliche Verdrahtung." },
    { title: "Fehlender Potentialausgleich", desc: "Sicherheitsrisiko bei Blitzeinschlag oder Isolationsfehlern." },
    { title: "Keine Kundeneinweisung", desc: "Sie verstehen Ihre Anlage nicht und wissen im Notfall nicht, was zu tun ist." },
    { title: "Keine Erweiterungsreserve", desc: "Wallbox, Speicher, Wärmepumpe später nicht möglich ohne aufwendigen Umbau." },
  ];

  const faqs = [
    {
      question: "Was genau ist eine PV-Abnahme und warum brauche ich sie?",
      answer: "Die PV-Abnahme ist die offizielle Prüfung und Inbetriebnahme Ihrer Photovoltaikanlage nach VDE-Normen. Sie ist Voraussetzung für die Anmeldung beim Netzbetreiber und im Marktstammdatenregister. Ohne Abnahme dürfen Sie keinen Strom einspeisen und haben im Schadensfall keinen Versicherungsschutz."
    },
    {
      question: "Warum brauche ich einen neuen Verteiler?",
      answer: "Der Zählerschrank ist das Herzstück Ihrer Elektroanlage. Ältere Verteiler entsprechen oft nicht mehr den aktuellen TAB (Technische Anschlussbedingungen) und bieten keinen Platz für Überspannungsschutz, Smart Meter oder Wallbox-Absicherung. Ein moderner Verteiler schützt Ihre Anlage und ermöglicht zukünftige Erweiterungen."
    },
    {
      question: "Was ist der Unterschied zwischen Ihnen und einem normalen Elektriker?",
      answer: "Ich bin spezialisiert auf PV-Inbetriebnahme, Energiesysteme und deren Dokumentation. Während ein Allround-Elektriker viele Bereiche abdeckt, konzentriere ich mich auf normgerechte Abnahmen, Netzbetreiber-Kommunikation und die Einweisung in moderne Energiesysteme. Das Ergebnis: saubere Dokumentation, rechtliche Sicherheit und ein Ansprechpartner, der Ihre Anlage wirklich versteht."
    },
    {
      question: "Warum kostet die Abnahme mehr als 'nur anschließen'?",
      answer: "Eine professionelle PV-Inbetriebnahme umfasst: VDE-konforme Prüfung aller Komponenten, Erstellung von Prüfprotokollen, Anmeldung beim Netzbetreiber, Eintrag im Marktstammdatenregister, Kundeneinweisung und Dokumentation. Das ist weit mehr als 'Kabel verbinden' – es ist die Grundlage für einen sicheren, legalen Betrieb."
    },
    {
      question: "Brauche ich wirklich einen Überspannungsschutz?",
      answer: "Ja, seit 2016 ist Überspannungsschutz für PV-Anlagen Pflicht (DIN VDE 0100-443). Bei einem Blitzeinschlag in der Nähe können ohne Schutz Wechselrichter, Speicher und angeschlossene Geräte zerstört werden. Die Kosten für den Schutz sind minimal im Vergleich zum möglichen Schaden."
    },
    {
      question: "Was ist das Marktstammdatenregister und warum muss ich mich dort anmelden?",
      answer: "Das Marktstammdatenregister (MaStR) ist das offizielle Register für Stromerzeugungsanlagen in Deutschland. Die Anmeldung ist gesetzlich vorgeschrieben. Ohne Eintrag drohen Bußgelder und der Verlust der EEG-Vergütung. Ich übernehme die Anmeldung für Sie."
    },
    {
      question: "Können Sie auch meinen PV-Speicher installieren?",
      answer: "Ja, die Integration von Batteriespeichern gehört zu meinen Kernleistungen. Ich plane die optimale Position, kümmere mich um die AC-seitige Installation, Einbindung ins Energiemanagement und die Parametrierung für maximalen Eigenverbrauch."
    },
    {
      question: "Was kostet eine Wallbox-Installation?",
      answer: "Die Kosten variieren je nach Zählerschrank-Zustand, Leitungslänge und Wallbox-Typ. Eine einfache Installation beginnt bei ca. 500€ Arbeitsleistung. Ich erstelle Ihnen gerne ein individuelles Angebot nach einer Vor-Ort-Besichtigung."
    },
    {
      question: "Mein Solateur hat die Anlage installiert – warum soll jemand anderes die Abnahme machen?",
      answer: "Viele Solarinstallateure konzentrieren sich auf die DC-Seite (Module, Montage). Die AC-seitige Abnahme, Netzbetreiber-Kommunikation und Dokumentation erfordert elektrotechnische Fachkenntnis. Als unabhängiger Prüfer stelle ich sicher, dass alles normgerecht ist – das schützt Sie und den Solateur."
    },
    {
      question: "Wie lange dauert eine PV-Abnahme?",
      answer: "Eine Standard-Abnahme dauert 2-4 Stunden vor Ort. Dazu kommt die Zeit für Dokumentation und Anmeldungen. Bei komplexen Anlagen oder notwendigen Nacharbeiten entsprechend länger. Ich plane ausreichend Zeit ein, um alles sorgfältig zu prüfen."
    },
    {
      question: "Was passiert, wenn bei der Prüfung Mängel gefunden werden?",
      answer: "Ich dokumentiere alle Mängel und erkläre Ihnen, was behoben werden muss. Kleine Dinge (z.B. fehlende Beschriftung) behebe ich direkt. Bei größeren Mängeln (z.B. falscher Leitungsquerschnitt) koordiniere ich mit Ihrem Solateur. Die Abnahme erfolgt erst, wenn alles sicher ist."
    },
    {
      question: "Kann ich meine alte PV-Anlage erweitern lassen?",
      answer: "Ja, Bestandsanlagen können oft um Module, Speicher oder Wallbox erweitert werden. Ich prüfe, ob Ihr Zählerschrank und die Elektroinstallation dafür vorbereitet sind, und plane die notwendigen Anpassungen."
    },
    {
      question: "Was ist ein Smart Meter und brauche ich das?",
      answer: "Ein Smart Meter ist ein intelligenter Stromzähler, der Ihren Verbrauch und Ihre Einspeisung in Echtzeit misst. Ab bestimmten Anlagengrößen ist er Pflicht. Ich bereite Ihren Zählerschrank für den Smart Meter vor und erkläre Ihnen die Vorteile für Ihr Energiemanagement."
    },
    {
      question: "Wie kann ich meinen Eigenverbrauch optimieren?",
      answer: "Es gibt viele Möglichkeiten: Speicher, zeitgesteuerte Verbraucher (Waschmaschine, Spülmaschine), Heizstab für Warmwasser, Wallbox mit PV-Überschussladung. Ich berate Sie, welche Maßnahmen bei Ihrer Anlage am sinnvollsten sind."
    },
    {
      question: "Was ist der Potentialausgleich und warum ist er wichtig?",
      answer: "Der Potentialausgleich verbindet alle metallischen Teile Ihrer Anlage mit der Erdung. Das verhindert gefährliche Spannungen bei Blitzeinschlag oder Isolationsfehlern. Bei PV-Anlagen auf dem Dach ist das besonders wichtig und wird bei der Abnahme geprüft."
    },
    {
      question: "Bieten Sie auch Wartung an?",
      answer: "Ja, ich biete regelmäßige Kontrollen Ihrer Anlage an. Dabei prüfe ich alle sicherheitsrelevanten Komponenten, aktualisiere bei Bedarf die Firmware und stelle sicher, dass Ihre Anlage optimal läuft."
    },
    {
      question: "Was mache ich, wenn meine Anlage eine Störung hat?",
      answer: "Bei der Inbetriebnahme weise ich Sie in Ihre Anlage ein, sodass Sie einfache Störungen selbst beheben können. Für komplexere Probleme stehe ich als Ansprechpartner zur Verfügung und kann die meisten Fehler schnell diagnostizieren."
    },
    {
      question: "Arbeiten Sie auch am Wochenende?",
      answer: "Nach Absprache ja. Ich verstehe, dass viele Berufstätige unter der Woche keine Zeit haben. Sprechen Sie mich einfach an, und wir finden einen Termin, der für Sie passt."
    },
    {
      question: "In welchem Umkreis sind Sie tätig?",
      answer: "Ich bin im Umkreis von 100 km um Eggenfelden tätig. Das umfasst Teile von Niederbayern und Oberbayern. Für größere Projekte bin ich nach Absprache auch weiter entfernt verfügbar."
    },
    {
      question: "Wie kann ich Sie am besten erreichen?",
      answer: "Am schnellsten erreichen Sie mich per WhatsApp oder Telefon unter +49 176 41234 614. Für ausführliche Anfragen nutzen Sie gerne E-Mail: info@enthida.de. Ich melde mich in der Regel innerhalb von 24 Stunden."
    }
  ];

  const references = [
    { img: "https://static.prod-images.emergentagent.com/jobs/d607ac6b-4513-4b85-be87-20e8a661ad1d/images/8f0d2e22d9fff561149ff980148efee607e21d7b5776e31a443b82ac34179e85.png", title: "Einfamilienhaus – 10 kWp", desc: "Komplette PV-Abnahme inkl. Zählerschrank-Modernisierung", quote: "Endlich alles sauber dokumentiert!", category: "pv-abnahme" },
    { img: "https://images.pexels.com/photos/32387194/pexels-photo-32387194.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", title: "Neubau mit Energiesystem", desc: "PV, Speicher und Wallbox aus einer Hand", quote: "Zukunftssicher geplant.", category: "speicher" },
    { img: "https://images.unsplash.com/photo-1754620906703-496ec2451744?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBwYW5lbHxlbnwwfHx8fDE3NzU3Mjk1MDN8MA&ixlib=rb-4.1.0&q=85", title: "Verteilerumbau", desc: "Komplette Zählerschrank-Modernisierung für PV-Anlage", quote: "Jetzt ist alles normgerecht.", category: "verteiler" },
    { img: "https://images.pexels.com/photos/9875441/pexels-photo-9875441.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", title: "Wallbox 22kW", desc: "Installation mit Lastmanagement und PV-Überschussladung", quote: "Laden mit eigenem Sonnenstrom!", category: "wallbox" },
    { img: "https://images.pexels.com/photos/8853537/pexels-photo-8853537.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", title: "Überspannungsschutz", desc: "Nachrüstung Typ 1+2 Ableiter für Bestandsanlage", quote: "Endlich richtig abgesichert.", category: "ueberspannung" },
    { img: "https://images.pexels.com/photos/6970628/pexels-photo-6970628.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", title: "Smart Meter Installation", desc: "Intelligentes Messsystem mit Energiemonitoring", quote: "Volle Transparenz über unseren Verbrauch.", category: "smartmeter" },
  ];

  const refCategories = [
    { id: "alle", label: "Alle Projekte" },
    { id: "pv-abnahme", label: "PV-Abnahme" },
    { id: "wallbox", label: "Wallbox" },
    { id: "speicher", label: "Speicher" },
    { id: "verteiler", label: "Verteilerumbau" },
    { id: "ueberspannung", label: "Überspannungsschutz" },
    { id: "smartmeter", label: "Smart Meter" },
  ];

  const filteredReferences = activeRefCategory === "alle" 
    ? references 
    : references.filter(r => r.category === activeRefCategory);

  const nextSlide = () => setCurrentRefSlide((prev) => (prev + 1) % filteredReferences.length);
  const prevSlide = () => setCurrentRefSlide((prev) => (prev - 1 + filteredReferences.length) % filteredReferences.length);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero */}
      <section className="relative pt-32 pb-32">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/d607ac6b-4513-4b85-be87-20e8a661ad1d/images/b709392042161b55856c9b63d53c7e53b4c60478295f6b6f124bbd8c04b73dfc.png)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Für Privatkunden</p>
          <h1 className="text-4xl md:text-6xl tracking-tighter font-medium text-white mb-6">
            Ihr Spezialist für
            <br />
            <span className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">moderne Energiesysteme.</span>
          </h1>
          <p className="text-lg text-[#a1a1aa] max-w-2xl leading-relaxed mb-8">
            PV-Abnahme, Inbetriebnahme, Wallbox, Speicher – strukturiert, normgerecht und mit Weitblick. 
            Für ein Energiesystem, das Sie verstehen und dem Sie vertrauen können.
          </p>
          
          {/* Hero Contact + Quick-Check */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-8">
            <div className="relative inline-block" ref={heroContactRef}>
              <button 
                onClick={() => setIsHeroContactOpen(!isHeroContactOpen)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-[1.02] shadow-[0_0_20px_rgba(0,212,170,0.3)]"
                data-testid="privat-hero-cta"
              >
                <Phone size={20} />
                Jetzt Kontakt aufnehmen
                <ChevronDown size={18} className={`transition-transform duration-300 ${isHeroContactOpen ? "rotate-180" : ""}`} />
            </button>
            
            {isHeroContactOpen && (
              <div className="absolute left-0 top-full mt-3 w-72 bg-[#121212] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-fadeIn z-50">
                <div className="p-3 space-y-1">
                  <a href="tel:+4917641234614" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <Phone size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Telefon</p>
                      <p className="text-[#00d4aa] text-xs">+49 176 41234 614</p>
                    </div>
                  </a>
                  <a href="mailto:info@enthida.de" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <Mail size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">E-Mail</p>
                      <p className="text-[#00d4aa] text-xs">info@enthida.de</p>
                    </div>
                  </a>
                  <a href="https://wa.me/4917641234614" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors group">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center">
                      <MessageCircle size={16} className="text-[#00d4aa]" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">WhatsApp</p>
                      <p className="text-[#00d4aa] text-xs">Schnelle Antwort</p>
                    </div>
                  </a>
                </div>
              </div>
            )}
            </div>
            
            {/* Sekundärer Anker zum Anlagen-Check */}
            <a
              href="#anlagen-check"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm text-white text-sm font-medium hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300"
              data-testid="privat-hero-quickcheck"
            >
              <ListChecks size={16} className="text-[#00d4aa]" />
              Schnell-Check starten
              <ArrowRight size={14} className="text-[#71717a] group-hover:text-[#00d4aa] group-hover:translate-x-0.5 transition-all" />
            </a>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <CheckCircle2 size={18} className="text-[#00d4aa]" />
              <span>VDE-zertifiziert</span>
            </div>
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Shield size={18} className="text-[#00d4aa]" />
              <span>Vollständig versichert</span>
            </div>
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Phone size={18} className="text-[#00d4aa]" />
              <span>Kostenlose Erstberatung</span>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky WhatsApp */}
      <a
        href="https://wa.me/4917641234614"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-5 py-3 rounded-full shadow-[0_0_30px_rgba(0,212,170,0.4)] hover:scale-105 transition-all duration-300 flex items-center gap-2"
      >
        <MessageCircle size={20} />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>

      {/* Brand Message */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="bg-gradient-to-r from-[#121212] to-[#18181b] border border-white/10 rounded-2xl p-8 lg:p-12">
            <p className="text-2xl md:text-3xl text-white font-medium leading-relaxed">
              ENTHIDA steht für <span className="text-[#00d4aa]">strukturierte, saubere und verständliche Energiesysteme</span> – nicht nur für schnelles Anschließen.
            </p>
            <p className="text-[#a1a1aa] mt-4">
              Wir planen mit Weitblick, damit Ihre Anlage heute funktioniert und morgen erweiterbar ist.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 bg-[#0a0a0a]" id="vergleich">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Der Unterschied</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white">
              Warum ENTHIDA?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 relative">
              <div className="absolute top-4 right-4"><XCircle className="text-red-500/50" size={24} /></div>
              <h3 className="text-xl font-medium text-[#71717a] mb-6">Standard-Elektriker</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-[#71717a]"><XCircle size={20} className="text-red-500/50 shrink-0 mt-0.5" /><span>Schnell anschließen und fertig</span></li>
                <li className="flex items-start gap-3 text-[#71717a]"><XCircle size={20} className="text-red-500/50 shrink-0 mt-0.5" /><span>Keine spezialisierte PV-Expertise</span></li>
                <li className="flex items-start gap-3 text-[#71717a]"><XCircle size={20} className="text-red-500/50 shrink-0 mt-0.5" /><span>Papierkram bleibt bei Ihnen</span></li>
                <li className="flex items-start gap-3 text-[#71717a]"><XCircle size={20} className="text-red-500/50 shrink-0 mt-0.5" /><span>Keine Einweisung in die Anlage</span></li>
                <li className="flex items-start gap-3 text-[#71717a]"><XCircle size={20} className="text-red-500/50 shrink-0 mt-0.5" /><span>Keine Planung für Erweiterungen</span></li>
              </ul>
            </div>

            <div className="bg-[#18181b] border border-[#00d4aa]/30 rounded-2xl p-8 relative shadow-[0_0_30px_rgba(0,212,170,0.05)] scale-[1.02]">
              <div className="absolute top-4 right-4"><CheckCircle2 className="text-[#00d4aa]" size={24} /></div>
              <h3 className="text-xl font-medium text-white mb-6">ENTHIDA</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-white"><CheckCircle2 size={20} className="text-[#00d4aa] shrink-0 mt-0.5" /><span>VDE-konforme Prüfung + saubere Übergabe</span></li>
                <li className="flex items-start gap-3 text-white"><CheckCircle2 size={20} className="text-[#00d4aa] shrink-0 mt-0.5" /><span>Spezialisiert auf PV und Energiesysteme</span></li>
                <li className="flex items-start gap-3 text-white"><CheckCircle2 size={20} className="text-[#00d4aa] shrink-0 mt-0.5" /><span>Netzbetreiber + Marktstammdatenregister inklusive</span></li>
                <li className="flex items-start gap-3 text-white"><CheckCircle2 size={20} className="text-[#00d4aa] shrink-0 mt-0.5" /><span>Kundeneinweisung: Sie verstehen Ihre Anlage</span></li>
                <li className="flex items-start gap-3 text-white"><CheckCircle2 size={20} className="text-[#00d4aa] shrink-0 mt-0.5" /><span>Zukunftssichere Planung für Wallbox & Speicher</span></li>
              </ul>
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-[#00d4aa] font-medium text-sm">Das bedeutet für Sie: Sicherheit, keine bösen Überraschungen und ein gutes Gefühl.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Was wir prüfen */}
      <section className="py-24 bg-[#0a0a0a]" id="pruefung">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Technische Kompetenz</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Was wir bei Ihrer PV-Anlage wirklich prüfen
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Eine professionelle PV-Abnahme ist mehr als ein Blick auf den Wechselrichter. Hier sind alle Punkte, die wir bei jeder Inbetriebnahme sorgfältig kontrollieren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pruefpunkte.map((punkt, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-xl p-5 hover:border-[#00d4aa]/30 transition-all duration-300 group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 size={16} className="text-[#00d4aa]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">{punkt.title}</h3>
                    <p className="text-[#71717a] text-sm">{punkt.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Moderne Energiesysteme */}
      <section className="py-24 bg-[#0a0a0a]" id="energiesysteme">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Mehr als nur PV</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Moderne Energiesysteme
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Die Photovoltaikanlage ist erst der Anfang. Wir planen und installieren komplette Energiesysteme für maximale Eigenverbrauchsoptimierung und Unabhängigkeit.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {energiesysteme.map((system, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-2xl p-6 hover:border-[#00d4aa]/30 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,212,170,0.2)] transition-all">
                  <system.icon className="text-[#00d4aa]" size={24} />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{system.title}</h3>
                <p className="text-[#71717a] text-sm">{system.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Häufige Probleme */}
      <section className="py-24 bg-[#0a0a0a]" id="probleme">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Worauf Sie achten sollten</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Häufige Probleme bei PV-Anlagen
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Diese Mängel finden wir regelmäßig bei Bestandsanlagen. Wir beheben sie – oder sorgen dafür, dass sie bei Neuanlagen gar nicht erst entstehen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {probleme.map((problem, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-red-500/20 rounded-2xl p-6 hover:border-red-500/40 transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0">
                    <AlertTriangle size={20} className="text-red-500/70" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">{problem.title}</h3>
                    <p className="text-[#71717a] text-sm">{problem.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-[#0a0a0a]" id="ablauf">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">So läuft es ab</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white">
              Ihr Projekt bei ENTHIDA
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-[39px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-[#7ed321] to-[#00d4aa] hidden md:block" />
            <div className="space-y-8">
              {processSteps.map((step, index) => (
                <div key={step.num} className="flex gap-8 items-start group">
                  <div className="relative z-10 w-20 h-20 rounded-2xl bg-[#121212] border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#00d4aa]/50 transition-all duration-300">
                    <span className="text-2xl font-bold bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">{step.num}</span>
                  </div>
                  <div className="bg-[#121212] border border-white/10 rounded-2xl p-6 flex-1 group-hover:border-[#00d4aa]/30 transition-all duration-300 group-hover:-translate-y-1">
                    <div className="flex items-center gap-3 mb-2">
                      <step.icon className="text-[#00d4aa]" size={20} />
                      <h3 className="text-xl font-medium text-white">{step.title}</h3>
                    </div>
                    <p className="text-[#a1a1aa]">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* References Slideshow */}
      <section className="py-24 bg-[#0a0a0a]" id="referenzen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Beispielprojekte</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-8">
              Referenzen
            </h2>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {refCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => { setActiveRefCategory(cat.id); setCurrentRefSlide(0); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeRefCategory === cat.id 
                      ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black" 
                      : "bg-[#121212] text-[#a1a1aa] hover:text-white border border-white/10"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Slideshow */}
          <div className="relative">
            <div className="overflow-hidden rounded-3xl">
              <div className="relative aspect-[16/9] md:aspect-[21/9]">
                {filteredReferences.map((ref, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-all duration-500 ${
                      index === currentRefSlide ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full"
                    }`}
                  >
                    <img src={ref.img} alt={ref.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                      <span className="inline-block px-3 py-1 bg-[#00d4aa]/20 text-[#00d4aa] text-xs font-medium rounded-full mb-3 uppercase tracking-wider">
                        {refCategories.find(c => c.id === ref.category)?.label || ref.category}
                      </span>
                      <h3 className="text-2xl md:text-3xl font-medium text-white mb-2">{ref.title}</h3>
                      <p className="text-[#a1a1aa] mb-3">{ref.desc}</p>
                      <p className="text-[#00d4aa] italic">"{ref.quote}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button 
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-black/70 transition-all"
            >
              <ChevronRight size={24} />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {filteredReferences.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentRefSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentRefSlide ? "w-8 bg-[#00d4aa]" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-[#0a0a0a]" id="faq">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Häufige Fragen</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Alles, was Sie wissen sollten
            </h2>
            <p className="text-[#a1a1aa]">
              Verständliche Antworten auf die wichtigsten Fragen rund um PV-Abnahme, Energiesysteme und unsere Arbeitsweise.
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-xl overflow-hidden hover:border-[#00d4aa]/20 transition-colors"
              >
                <button
                  className="w-full p-5 flex items-center justify-between text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className={`font-medium pr-4 transition-colors ${openFaq === index ? "text-[#00d4aa]" : "text-white"}`}>
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`text-[#00d4aa] shrink-0 transition-transform duration-300 ${openFaq === index ? "rotate-180" : ""}`}
                    size={20}
                  />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === index ? "max-h-96" : "max-h-0"}`}>
                  <p className="px-5 pb-5 text-[#a1a1aa] leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center p-6 bg-[#121212] border border-white/10 rounded-2xl">
            <p className="text-[#a1a1aa] mb-4">Ihre Frage war nicht dabei? Ich erkläre Ihnen alles persönlich.</p>
            <a href="tel:+4917641234614" className="inline-flex items-center gap-2 text-[#00d4aa] font-medium hover:text-white transition-colors">
              <Phone size={18} />
              +49 176 41234 614
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 bg-[#0a0a0a]" id="preise">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#00d4aa] mb-4">Transparente Preise</p>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white">Kosten & Pakete</h2>
            <p className="text-[#a1a1aa] mt-4 max-w-2xl mx-auto">Faire Preise für professionelle Arbeit. Keine versteckten Kosten.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 hover:border-[#00d4aa]/30 transition-all duration-300">
              <h3 className="text-xl font-medium text-white mb-2">PV-Abnahme + Inbetriebnahme</h3>
              <p className="text-[#71717a] text-sm mb-6">Die Basis für Ihre PV-Anlage</p>
              <div className="mb-6"><span className="text-4xl font-bold text-white">ab 500€</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />VDE-konforme Prüfung</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Prüfprotokolle</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Netzbetreiber-Anmeldung</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Kundeneinweisung</li>
              </ul>
              <a href="tel:+4917641234614" className="block w-full text-center bg-[#18181b] border border-white/10 text-white font-semibold px-6 py-3 rounded-full hover:border-[#00d4aa]/50 transition-all">Anfragen</a>
            </div>

            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 hover:border-[#00d4aa]/30 transition-all duration-300">
              <h3 className="text-xl font-medium text-white mb-2">Wallbox Installation</h3>
              <p className="text-[#71717a] text-sm mb-6">Laden Sie Ihr E-Auto zuhause</p>
              <div className="mb-6"><span className="text-4xl font-bold text-white">nach Aufwand</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Wallbox-Anschluss</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Absicherung & Schutz</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />PV-Überschussladung</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Anmeldung Netzbetreiber</li>
              </ul>
              <a href="tel:+4917641234614" className="block w-full text-center bg-[#18181b] border border-white/10 text-white font-semibold px-6 py-3 rounded-full hover:border-[#00d4aa]/50 transition-all">Anfragen</a>
            </div>

            <div className="bg-[#18181b] border border-[#00d4aa]/30 rounded-2xl p-8 relative shadow-[0_0_30px_rgba(0,212,170,0.1)] scale-[1.02]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black text-xs font-bold px-4 py-1 rounded-full">EMPFOHLEN</div>
              <h3 className="text-xl font-medium text-white mb-2">Komplettpaket</h3>
              <p className="text-[#71717a] text-sm mb-6">Alles aus einer Hand</p>
              <div className="mb-6"><span className="text-4xl font-bold text-white">individuell</span></div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />PV-Abnahme + Inbetriebnahme</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Speicher-Integration</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Wallbox + Lastmanagement</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Smart Meter + Monitoring</li>
                <li className="flex items-center gap-2 text-[#a1a1aa]"><CheckCircle2 size={16} className="text-[#00d4aa]" />Zukunftssichere Planung</li>
              </ul>
              <a href="tel:+4917641234614" className="block w-full text-center bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-6 py-3 rounded-full hover:scale-[1.02] transition-all shadow-[0_0_20px_rgba(0,212,170,0.3)]">Anfragen</a>
            </div>
          </div>
        </div>
      </section>

      {/* Anlagen-Check Form */}
      <section className="py-24 bg-[#0a0a0a] relative overflow-hidden" id="anlagen-check">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 -left-32 w-96 h-96 bg-[#7ed321]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 -right-32 w-96 h-96 bg-[#00d4aa]/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-[#00d4aa]/20 rounded-full px-4 py-2 mb-6">
              <ListChecks size={14} className="text-[#00d4aa]" />
              <span className="text-[#a1a1aa] text-xs uppercase tracking-[0.18em] font-semibold">Persönliche Beratung</span>
            </div>
            <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
              Anlage <span className="bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">prüfen lassen</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg max-w-2xl mx-auto">
              Kurz beschreiben, ich melde mich persönlich – ehrlich und ohne Verkaufsdruck.
            </p>
          </div>

          <div className="relative bg-gradient-to-br from-[#121212] to-[#0e0e0e] border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/40">
            <div className="absolute -top-px left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#00d4aa]/50 to-transparent" />

            {/* Grid 1: Status + Größe */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">1</span>
                  Anlagenstatus
                </label>
                <div className="relative">
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full appearance-none bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 pr-10 text-white text-sm focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all cursor-pointer hover:border-white/20"
                    data-testid="b2c-form-status"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="Geplant">In Planung</option>
                    <option value="In Bau">Aktuell in Bau</option>
                    <option value="Fertig installiert">Fertig installiert</option>
                    <option value="Bestandsanlage">Bestandsanlage</option>
                    <option value="Unklar">Weiß ich nicht genau</option>
                  </select>
                  <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00d4aa] pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">2</span>
                  Anlagengröße
                </label>
                <div className="flex flex-wrap gap-2">
                  {["< 10 kWp", "10–20 kWp", "20+ kWp", "Unbekannt"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, groesse: opt })}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.groesse === opt
                          ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black border-transparent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-[#0a0a0a] text-[#a1a1aa] border-white/10 hover:border-[#00d4aa]/40 hover:text-white"
                      }`}
                      data-testid={`b2c-form-groesse-${opt.replace(/[^a-z0-9]/gi, "")}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Grid 2: Speicher + Termin */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">3</span>
                  Speicher vorhanden?
                </label>
                <div className="flex flex-wrap gap-2">
                  {["Ja", "Nein", "Geplant"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, speicher: opt })}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.speicher === opt
                          ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black border-transparent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-[#0a0a0a] text-[#a1a1aa] border-white/10 hover:border-[#00d4aa]/40 hover:text-white"
                      }`}
                      data-testid={`b2c-form-speicher-${opt}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">4</span>
                  Wunschtermin
                </label>
                <div className="flex flex-wrap gap-2">
                  {["Sofort", "Nächste 2 Wochen", "Diesen Monat", "Flexibel"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setForm({ ...form, termin: opt })}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                        form.termin === opt
                          ? "bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black border-transparent shadow-[0_0_15px_rgba(0,212,170,0.25)]"
                          : "bg-[#0a0a0a] text-[#a1a1aa] border-white/10 hover:border-[#00d4aa]/40 hover:text-white"
                      }`}
                      data-testid={`b2c-form-termin-${opt.replace(/[^a-z0-9]/gi, "")}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Themen Checkboxen */}
            <div className="mt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-3">
                <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">5</span>
                Worum geht's?
                <span className="text-[#71717a] text-xs font-normal">(Mehrfachauswahl)</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { val: "VDE-Abnahme", icon: Shield },
                  { val: "Inbetriebnahme", icon: Zap },
                  { val: "Einweisung", icon: HelpCircle },
                  { val: "Dokumentation", icon: FileCheck },
                  { val: "Zweitmeinung", icon: CheckCircle2 },
                  { val: "Fehler beheben", icon: AlertTriangle },
                ].map(({ val, icon: Icon }) => {
                  const active = form.themen.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => toggleThema(val)}
                      className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm font-medium transition-all duration-200 text-left ${
                        active
                          ? "bg-gradient-to-br from-[#7ed321]/10 to-[#00d4aa]/10 border-[#00d4aa]/50 text-white"
                          : "bg-[#0a0a0a] border-white/10 text-[#a1a1aa] hover:border-white/20 hover:text-white"
                      }`}
                      data-testid={`b2c-form-thema-${val.toLowerCase().replace(/[^a-z0-9]/gi, "")}`}
                    >
                      <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${active ? "bg-gradient-to-br from-[#7ed321] to-[#00d4aa] border-transparent" : "border-white/20"}`}>
                        {active && <CheckCircle2 size={12} className="text-black" strokeWidth={3} />}
                      </span>
                      <Icon size={14} className={active ? "text-[#00d4aa]" : "text-[#71717a]"} />
                      <span>{val}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name + Ort */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">6</span>
                  Name <span className="text-[#71717a] text-xs font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ihr Name"
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all hover:border-white/20"
                    data-testid="b2c-form-name"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                  <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">7</span>
                  Ort <span className="text-[#71717a] text-xs font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                  <input
                    type="text"
                    value={form.ort}
                    onChange={(e) => setForm({ ...form, ort: e.target.value })}
                    placeholder="z.B. Eggenfelden, Landshut"
                    className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all hover:border-white/20"
                    data-testid="b2c-form-ort"
                  />
                </div>
              </div>
            </div>

            {/* Nachricht */}
            <div className="mt-6">
              <label className="flex items-center gap-2 text-sm font-medium text-white mb-2">
                <span className="w-6 h-6 rounded-md bg-gradient-to-br from-[#7ed321]/20 to-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-xs font-bold">8</span>
                Nachricht <span className="text-[#71717a] text-xs font-normal">(optional)</span>
              </label>
              <textarea
                value={form.nachricht}
                onChange={(e) => setForm({ ...form, nachricht: e.target.value })}
                rows={3}
                placeholder="Beschreiben Sie kurz Ihr Anliegen oder stellen Sie eine Frage …"
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-[#52525b] focus:outline-none focus:border-[#00d4aa]/50 focus:ring-2 focus:ring-[#00d4aa]/10 transition-all hover:border-white/20 resize-none"
                data-testid="b2c-form-nachricht"
              />
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleWhatsApp}
                className="group flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] text-black font-semibold px-6 py-4 rounded-xl transition-all duration-300 hover:scale-[1.01] shadow-[0_0_25px_rgba(0,212,170,0.3)]"
                data-testid="b2c-form-submit-whatsapp"
              >
                <MessageCircle size={18} />
                Anlage prüfen lassen
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                type="button"
                onClick={handleEmail}
                className="group flex-1 flex items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-semibold px-6 py-4 rounded-xl transition-all duration-300 hover:border-[#00d4aa]/50 hover:bg-white/10"
                data-testid="b2c-form-submit-email"
              >
                <Send size={18} className="text-[#00d4aa]" />
                Per E-Mail senden
              </button>
            </div>

            <p className="mt-4 text-[#71717a] text-xs text-center">
              Ihre Angaben werden direkt an mich übermittelt. Keine Speicherung, keine Weitergabe an Dritte.
            </p>
          </div>

          {/* Trust Row */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: User, label: "Persönliche Beratung" },
              { icon: CheckCircle2, label: "Keine versteckten Kosten" },
              { icon: Shield, label: "VDE-konform geprüft" },
              { icon: HelpCircle, label: "Verständliche Erklärung" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-[#121212]/60 border border-white/5 rounded-xl px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7ed321]/15 to-[#00d4aa]/15 flex items-center justify-center shrink-0">
                  <item.icon size={15} className="text-[#00d4aa]" />
                </div>
                <span className="text-[#a1a1aa] text-xs md:text-sm font-medium leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#7ed321]/10 to-[#00d4aa]/10 rounded-full blur-3xl animate-pulse" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="bg-gradient-to-br from-[#121212] to-[#0a0a0a] border border-white/10 rounded-3xl p-10 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#7ed321]/20 via-transparent to-[#00d4aa]/20 opacity-50" />
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#00d4aa]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#7ed321]/20 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-[#00d4aa] rounded-full animate-pulse" />
                <span className="text-[#a1a1aa] text-sm">Kapazitäten verfügbar</span>
              </div>
              
              <h2 className="text-3xl md:text-5xl tracking-tighter font-medium text-white mb-4">
                Bereit für den
                <span className="block bg-gradient-to-r from-[#7ed321] to-[#00d4aa] bg-clip-text text-transparent">nächsten Schritt?</span>
              </h2>
              <p className="text-lg text-[#a1a1aa] mb-10 max-w-xl mx-auto">
                Lassen Sie uns über Ihr Projekt sprechen. Unverbindlich und kostenlos.
              </p>
              
              <div className="space-y-4 mb-8">
                <a href="tel:+4917641234614" className="group flex items-center justify-center gap-4 p-6 bg-gradient-to-r from-[#7ed321] to-[#00d4aa] rounded-2xl hover:scale-[1.02] transition-all duration-300 shadow-[0_0_30px_rgba(0,212,170,0.3)]">
                  <div className="w-14 h-14 rounded-2xl bg-black/20 flex items-center justify-center">
                    <Phone size={28} className="text-black" />
                  </div>
                  <div className="text-left">
                    <p className="text-black font-bold text-xl">Jetzt anrufen</p>
                    <p className="text-black/70">+49 176 41234 614</p>
                  </div>
                  <ArrowRight size={24} className="text-black ml-auto group-hover:translate-x-1 transition-transform" />
                </a>
                
                <div className="grid grid-cols-2 gap-4">
                  <a href="mailto:info@enthida.de" className="group flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Mail size={20} className="text-[#00d4aa]" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">E-Mail</p>
                      <p className="text-[#a1a1aa] text-xs">info@enthida.de</p>
                    </div>
                  </a>
                  
                  <a href="https://wa.me/4917641234614" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-[#00d4aa]/50 hover:bg-white/10 transition-all duration-300">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <MessageCircle size={20} className="text-[#00d4aa]" />
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium text-sm">WhatsApp</p>
                      <p className="text-[#a1a1aa] text-xs">Schnelle Antwort</p>
                    </div>
                  </a>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-6 text-[#71717a] text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#00d4aa]" /><span>Kostenlose Beratung</span></div>
                <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#00d4aa]" /><span>24h Reaktionszeit</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivatkundenPage;
